import React, { useRef, Suspense, lazy, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { useAppContext, ACTIONS } from '@components/Contexts/AppContext';
import { DOCUMENT_HEADER, PREVIEW_ID, VIEW_MODE } from '@src/utils/constants';
import { getHeaderPortalRoot, getScrollOffset } from '@src/utils/frameHelpers';
import Portal from '@components/Portal';
import feedbackPinStyle from '@components/FeedbackPin/FeedbackPinStyle';
import s from '../SiteRenderer.scss';

const FeedbackPin = lazy(() => import('@components/FeedbackPin/FeedbackPin'));

interface IClassicRendererProps {
  handleFrameLoaded: (
    previewEl: React.MutableRefObject<HTMLIFrameElement>,
  ) => void;
}

const ClassicRenderer = ({ handleFrameLoaded }: IClassicRendererProps) => {
  const {
    dispatch,
    state: { loading, siteUrl, viewMode, pageId, comments, dropdownMenuExists },
  } = useAppContext();

  const previewEl = useRef() as React.MutableRefObject<HTMLIFrameElement>;

  const renderPortals = useCallback(() => {
    const headerComments = _.filter(comments, comment => {
      const { breakpoint } = comment.commentLocation;
      return (
        comment.commentLocation.pageId === DOCUMENT_HEADER &&
        breakpoint.id === viewMode
      );
    });
    const currentPageComments = _.filter(comments, comment => {
      const { breakpoint } = comment.commentLocation;
      if (comment.commentLocation.innerPath) {
        return (
          pageId === comment.commentLocation.pageId &&
          breakpoint.id === viewMode &&
          comment.commentLocation.innerPath ===
            previewEl.current?.contentWindow?.location?.pathname
        );
      }
      return (
        pageId === comment.commentLocation.pageId && breakpoint.id === viewMode
      );
    });

    return (
      <>
        <Portal
          closeAllComments={() =>
            dispatch({
              type: ACTIONS.DE_ACTIVATE_COMMENTS_AND_CLEAN_NEW_COMMENTS,
            })
          }
          portalRoot={getHeaderPortalRoot(previewEl)}
        >
          <style>{feedbackPinStyle()}</style>
          {headerComments.map(comment => (
            <Suspense fallback="">
              <FeedbackPin
                isHeader={true}
                key={comment.id}
                comment={comment}
                pageYOffset={previewEl.current.contentWindow?.pageYOffset!}
                previewEl={previewEl}
              />
            </Suspense>
          ))}
        </Portal>
        <Portal
          closeAllComments={() =>
            dispatch({
              type: ACTIONS.DE_ACTIVATE_COMMENTS_AND_CLEAN_NEW_COMMENTS,
            })
          }
          portalRoot={previewEl.current.contentDocument!.getElementById(pageId)}
        >
          <style>{feedbackPinStyle()}</style>
          {currentPageComments.map(comment => (
            <Suspense fallback="">
              <FeedbackPin
                key={comment.id}
                comment={comment}
                pageYOffset={previewEl.current.contentWindow?.pageYOffset!}
                previewEl={previewEl}
              />
            </Suspense>
          ))}
        </Portal>
      </>
    );
  }, [comments, previewEl, pageId, viewMode, dispatch]);

  function showIfInMobile() {
    return viewMode === VIEW_MODE.MOBILE ? s.show : s.hide;
  }

  function getMobileLeft(styleOffset: number) {
    if (viewMode === VIEW_MODE.MOBILE) {
      const offset = styleOffset + getScrollOffset(previewEl.current) / 2;
      return {
        left: `calc(50% - ${offset}px)`,
      };
    }
    return { left: 0 };
  }

  function getMobileClassIfInMobile() {
    return viewMode === VIEW_MODE.MOBILE ? s.frameMobile : '';
  }

  function hideIfSwitchingDevice() {
    return loading ? s.hide : s.show;
  }

  const iframeSrc = useMemo(
    () =>
      dropdownMenuExists && viewMode === VIEW_MODE.DESKTOP
        ? `${siteUrl}&rerenderFordropdownMenu=true`
        : viewMode === VIEW_MODE.DESKTOP
        ? siteUrl
        : `${siteUrl}&showMobileView=true`,
    [siteUrl, dropdownMenuExists, viewMode],
  );

  function renderClassicPreview() {
    return (
      <>
        <div
          style={getMobileLeft(186)}
          className={`${s.mobile} ${showIfInMobile()}`}
        ></div>
        <div
          style={getMobileLeft(197)}
          className={`${s.mobileBg} ${showIfInMobile()}`}
        ></div>
        <iframe
          title="feedback-viewer"
          id={PREVIEW_ID}
          ref={previewEl}
          className={`${
            viewMode === VIEW_MODE.DESKTOP
              ? s.editorIframe
              : s.editorIframeMobile
          } ${getMobileClassIfInMobile()} ${hideIfSwitchingDevice()}`}
          src={iframeSrc}
          data-hook="site-renderer-preview"
          onLoad={() => handleFrameLoaded(previewEl)}
        ></iframe>
        <div
          style={getMobileLeft(197)}
          className={`${s.mobileFooter} ${showIfInMobile()}`}
        ></div>
        {comments.length > 0 && !loading ? renderPortals() : <></>}
      </>
    );
  }

  return (
    <div className={s.siteRenderer} data-hook="site-renderer">
      <div className={s.iframeWrapper}>
        <div className={s.iframeContainer}>{renderClassicPreview()}</div>
      </div>
    </div>
  );
};

export default ClassicRenderer;
